import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '../primitives/button';
import { cn } from '../lib/cn';
import { useUiMessages } from '../i18n/messages';

export interface BrandSwitcherOption {
  readonly brandId: string;
  readonly name: string;
  readonly workspaceName?: string;
}

export interface BrandSwitcherProps {
  readonly brands: readonly BrandSwitcherOption[];
  readonly currentBrandId: string | null;
  readonly onSelect: (brandId: string) => void;
  readonly className?: string;
}

/** Cross-brand context switcher; every screen below it is scoped to the selection. */
export function BrandSwitcher({ brands, currentBrandId, onSelect, className }: BrandSwitcherProps) {
  const messages = useUiMessages();
  const copy = messages.brandSwitcher;
  const current = brands.find((b) => b.brandId === currentBrandId) ?? null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="outline"
          className={cn('w-56 justify-between', className)}
          aria-label={copy.switchBrand}
        >
          <span className="truncate">{current ? current.name : copy.selectBrand}</span>
          <ChevronsUpDown aria-hidden="true" className="size-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          className="z-50 min-w-56 overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
        >
          {brands.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">{copy.noBrands}</div>
          ) : null}
          {brands.map((brand) => (
            <DropdownMenu.Item
              key={brand.brandId}
              onSelect={() => onSelect(brand.brandId)}
              className={cn(
                'flex cursor-pointer items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm',
                'outline-none focus:bg-accent focus:text-accent-foreground',
              )}
            >
              <span className="flex flex-col">
                <span>{brand.name}</span>
                {brand.workspaceName ? (
                  <span className="text-xs text-muted-foreground">{brand.workspaceName}</span>
                ) : null}
              </span>
              {brand.brandId === currentBrandId ? (
                <Check aria-hidden="true" className="size-4" />
              ) : null}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
